import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  FileText,
  LayoutTemplate,
  Pencil,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Spinner,
  cn,
  useToast,
} from '../../ui';
import { MarkdownView } from '../Knowledge/MarkdownView';
import { useTaskTemplates } from '../../features/taskTemplates/useTaskTemplates';
import { draftFromTemplate } from '../../features/taskTemplates/taskTemplateModel';
import type {
  TaskTemplateDraft,
  TaskTemplateSummary,
} from '../../features/taskTemplates/types';
import { TaskTemplateEditorModal } from './TaskTemplateEditorModal';
import { OwnersOfGoalsCard } from './OwnersOfGoalsCard';

/** One selectable template entry in the left rail. */
function EntryButton({
  entry,
  active,
  onSelect,
}: {
  entry: TaskTemplateSummary;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      onClick={onSelect}
      className={cn(
        'w-full rounded-xl border px-3 py-2 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        active
          ? 'border-accent bg-surface-3'
          : 'border-surface-border bg-surface-1 hover:border-zinc-600',
      )}
    >
      <span className="flex items-start gap-2">
        <FileText
          className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500"
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-zinc-100">
            {entry.name}
          </span>
          {entry.isDefault && (
            <span className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge tone="accent">
                <Star className="mr-1 h-3 w-3" aria-hidden="true" />
                Default
              </Badge>
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

/**
 * TaskTemplatesPage — manage reusable task templates and define the owners of
 * each stream/goal.
 *
 * Templates are listed (default-first) in a left rail; selecting one renders its
 * Markdown inline via {@link MarkdownView}, with edit / delete / "set as
 * default" actions. Create / edit run through {@link TaskTemplateEditorModal};
 * delete through {@link ConfirmDialog}. The built-in default template is seeded
 * idempotently on first load. The {@link OwnersOfGoalsCard} renders below.
 *
 * Reads/writes exclusively through {@link useTaskTemplates} (hook → store → api
 * facade); it never touches the network directly. Tenant-scoped automatically.
 */
export function TaskTemplatesPage() {
  const { toast } = useToast();
  const {
    templates,
    selectedId,
    selected,
    selecting,
    loading,
    error,
    select,
    create,
    update,
    remove,
    setDefault,
    ensureDefault,
  } = useTaskTemplates();

  // Idempotently seed the built-in default template on first load.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    void ensureDefault();
  }, [ensureDefault]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<TaskTemplateDraft | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const openCreate = () => {
    setEditing(undefined);
    setEditorOpen(true);
  };

  const openEdit = () => {
    if (!selected) return;
    setEditing(draftFromTemplate(selected));
    setEditorOpen(true);
  };

  const onSave = async (draft: TaskTemplateDraft) => {
    setSaving(true);
    try {
      if (editing && selectedId) {
        await update(selectedId, draft);
        toast({ tone: 'success', title: 'Template updated' });
      } else {
        await create(draft);
        toast({ tone: 'success', title: 'Template created' });
      }
      setEditorOpen(false);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Save failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!selectedId) return;
    setDeleting(true);
    try {
      await remove(selectedId);
      setConfirmOpen(false);
      toast({ tone: 'success', title: 'Template deleted' });
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Delete failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const onSetDefault = async () => {
    if (!selectedId) return;
    try {
      await setDefault(selectedId);
      toast({ tone: 'success', title: 'Default template set' });
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not set default',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  const newButton = (
    <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
      New template
    </Button>
  );

  return (
    <div>
      <PageHeader
        icon={<LayoutTemplate className="h-5 w-5" />}
        title="Task Templates"
        description="Reusable Markdown skeletons for authoring work items, plus the owners of each stream/goal."
        actions={newButton}
      />

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading templates" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load templates"
          description={error}
        />
      ) : templates.length === 0 ? (
        <EmptyState
          icon={<LayoutTemplate className="h-6 w-6" />}
          title="No templates yet"
          description="Create the first reusable task template. Write Markdown inline; mark one as the default."
          action={newButton}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[20rem_1fr]">
          <nav
            aria-label="Task templates"
            className="space-y-2 lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1"
          >
            <ul className="space-y-2">
              {templates.map((entry) => (
                <li key={entry.id}>
                  <EntryButton
                    entry={entry}
                    active={entry.id === selectedId}
                    onSelect={() => void select(entry.id)}
                  />
                </li>
              ))}
            </ul>
          </nav>

          <div className="rounded-2xl border border-surface-border bg-surface-2/50 p-4">
            {selected ? (
              <div className="space-y-4">
                <div className="flex flex-col gap-3 border-b border-surface-border pb-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-zinc-100">
                      {selected.name}
                    </h3>
                    {selected.isDefault && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <Badge tone="accent">
                          <Star className="mr-1 h-3 w-3" aria-hidden="true" />
                          Default
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {!selected.isDefault && (
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<Star className="h-3.5 w-3.5" />}
                        onClick={() => void onSetDefault()}
                      >
                        Set as default
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<Pencil className="h-3.5 w-3.5" />}
                      onClick={openEdit}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                      onClick={() => setConfirmOpen(true)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
                {selecting ? (
                  <div className="flex items-center justify-center py-24">
                    <Spinner label="Loading template" />
                  </div>
                ) : (
                  <MarkdownView markdown={selected.content} />
                )}
              </div>
            ) : selecting ? (
              <div className="flex items-center justify-center py-24">
                <Spinner label="Loading template" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-3 text-accent">
                  <FileText className="h-6 w-6" aria-hidden="true" />
                </div>
                <p className="text-sm font-medium text-zinc-300">
                  Select a template
                </p>
                <p className="mt-1 max-w-xs text-xs text-zinc-500">
                  Pick a template on the left to render its Markdown, edit it, or
                  mark it as the default.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-8">
        <OwnersOfGoalsCard />
      </div>

      <TaskTemplateEditorModal
        open={editorOpen}
        initial={editing}
        saving={saving}
        onClose={() => !saving && setEditorOpen(false)}
        onSave={(draft) => void onSave(draft)}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Delete template?"
        message="This template will be removed from the list. This can't be undone."
        loading={deleting}
        onConfirm={() => void onConfirmDelete()}
        onClose={() => !deleting && setConfirmOpen(false)}
      />
    </div>
  );
}

export default TaskTemplatesPage;
